import React, { useState, useRef, DragEvent } from 'react';
import expensesApi from '../api/expenses';

interface InvoiceUploaderProps {
  onSaved?: () => void;
}

type FileStatus = 'pending' | 'processing' | 'success' | 'error';

interface FileItem {
  id: string;
  file: File;
  previewUrl: string | null;
  isPdf: boolean;
  status: FileStatus;
  errorMessage?: string;
}

const InvoiceUploader: React.FC<InvoiceUploaderProps> = ({ onSaved }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles: FileItem[] = [];

    for (const file of newFiles) {
      const isImage = file.type.startsWith('image/');
      const isPdfFile = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

      if (isImage || isPdfFile) {
        validFiles.push({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          previewUrl: isImage ? URL.createObjectURL(file) : null,
          isPdf: isPdfFile,
          status: 'pending',
        });
      }
    }

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      addFiles(Array.from(selectedFiles));
    }
    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (id: string) => {
    setFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.previewUrl) {
        URL.revokeObjectURL(file.previewUrl);
      }
      return prev.filter(f => f.id !== id);
    });
  };

  const clearCompleted = () => {
    setFiles(prev => {
      prev.forEach(f => {
        if (f.previewUrl && (f.status === 'success' || f.status === 'error')) {
          URL.revokeObjectURL(f.previewUrl);
        }
      });
      return prev.filter(f => f.status !== 'success' && f.status !== 'error');
    });
  };

  const clearAll = () => {
    files.forEach(f => {
      if (f.previewUrl) {
        URL.revokeObjectURL(f.previewUrl);
      }
    });
    setFiles([]);
  };

  const processAllFiles = async () => {
    const pendingFiles = files.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsProcessing(true);
    let hasSuccess = false;

    for (const fileItem of pendingFiles) {
      // Update status to processing
      setFiles(prev => prev.map(f =>
        f.id === fileItem.id ? { ...f, status: 'processing' as FileStatus } : f
      ));

      try {
        const formData = new FormData();
        formData.append('invoice', fileItem.file);

        const parsed = await expensesApi.previewInvoice(formData);
        await expensesApi.create(parsed);

        // Update status to success
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'success' as FileStatus } : f
        ));
        hasSuccess = true;
      } catch (error: any) {
        console.error('Error processing invoice:', error);
        const errorMessage = error.response?.data?.message || 'Ошибка при обработке';

        // Update status to error
        setFiles(prev => prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'error' as FileStatus, errorMessage } : f
        ));
      }
    }

    setIsProcessing(false);

    if (hasSuccess) {
      onSaved?.();
    }
  };

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  const getStatusIcon = (status: FileStatus) => {
    switch (status) {
      case 'processing':
        return (
          <div className="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        );
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const getStatusBg = (status: FileStatus) => {
    switch (status) {
      case 'processing':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-white border-gray-200';
    }
  };

  return (
    <div>
      <p className="text-sm text-gray-500 mb-4">
        Загрузите фото или PDF чеков, счетов или инвойсов для автоматического распознавания
      </p>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleInputChange}
          accept="image/*,.pdf,application/pdf"
          multiple
          className="hidden"
        />
        <div className="text-gray-600">
          <svg
            className="mx-auto h-10 w-10 text-gray-400"
            stroke="currentColor"
            fill="none"
            viewBox="0 0 48 48"
          >
            <path
              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="mt-2">
            <span className="font-medium text-blue-600">Нажмите для загрузки</span> или перетащите файлы
          </p>
          <p className="text-sm text-gray-500">JPG, PNG, PDF до 10MB (можно несколько)</p>
        </div>
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Файлы ({files.length})
            </span>
            <div className="flex gap-2">
              {(successCount > 0 || errorCount > 0) && (
                <button
                  onClick={clearCompleted}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Очистить завершённые
                </button>
              )}
              <button
                onClick={clearAll}
                disabled={isProcessing}
                className="text-xs text-red-500 hover:text-red-700 disabled:text-gray-400"
              >
                Очистить все
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {files.map((fileItem) => (
              <div
                key={fileItem.id}
                className={`flex items-center gap-3 p-3 rounded-lg border ${getStatusBg(fileItem.status)}`}
              >
                {/* Preview */}
                <div className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                  {fileItem.isPdf ? (
                    <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zM6 4h7v5h5v11H6V4z" />
                    </svg>
                  ) : fileItem.previewUrl ? (
                    <img
                      src={fileItem.previewUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>

                {/* File info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {fileItem.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(fileItem.file.size / 1024).toFixed(1)} KB
                    {fileItem.status === 'error' && fileItem.errorMessage && (
                      <span className="text-red-500 ml-2">{fileItem.errorMessage}</span>
                    )}
                    {fileItem.status === 'success' && (
                      <span className="text-green-500 ml-2">Распознано</span>
                    )}
                    {fileItem.status === 'processing' && (
                      <span className="text-blue-500 ml-2">Распознавание...</span>
                    )}
                  </p>
                </div>

                {/* Status icon */}
                <div className="flex-shrink-0">
                  {getStatusIcon(fileItem.status)}
                </div>

                {/* Remove button (only for pending files) */}
                {fileItem.status === 'pending' && (
                  <button
                    onClick={() => removeFile(fileItem.id)}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Summary and actions */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {pendingCount > 0 && <span className="mr-3">Ожидает: {pendingCount}</span>}
              {successCount > 0 && <span className="mr-3 text-green-600">Успешно: {successCount}</span>}
              {errorCount > 0 && <span className="text-red-600">Ошибки: {errorCount}</span>}
            </div>

            {pendingCount > 0 && (
              <button
                onClick={processAllFiles}
                disabled={isProcessing}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Распознавание...
                  </>
                ) : (
                  <>
                    Распознать все ({pendingCount})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceUploader;
